import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Trophy, Loader2 } from 'lucide-react';
import { getIconByName } from '@/components/team/performanceMetricsConfig';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { TeamPerformanceMetric } from '@/types/team';

interface WinPerformanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unitId?: string;
  onConfirm: (metricNames: string[], notes?: string) => void;
}

export const WinPerformanceDialog = ({
  open,
  onOpenChange,
  unitId,
  onConfirm
}: WinPerformanceDialogProps) => {
  const [metrics, setMetrics] = useState<TeamPerformanceMetric[]>([]);
  const [selectedMetricIds, setSelectedMetricIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);

  useEffect(() => {
    const loadMetrics = async () => {
      if (!open) return;
      
      setLoading(true);
      try {
        // Get user's team
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('team_id')
          .eq('user_id', user.id)
          .single();

        if (!profile?.team_id) {
          setLoading(false);
          return;
        }

        setTeamId(profile.team_id);

        // Load active metrics for the team
        const { data: metricsData } = await supabase
          .from('team_performance_metrics')
          .select('*')
          .eq('team_id', profile.team_id)
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (metricsData) {
          setMetrics(metricsData);
        }
      } catch (error) {
        console.error('Error loading metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, [open]);

  const handleMetricToggle = useCallback((metricId: string) => {
    console.log('📊 Toggling metric:', metricId);
    setSelectedMetricIds(prev => {
      const newIds = prev.includes(metricId)
        ? prev.filter(id => id !== metricId)
        : [...prev, metricId];
      console.log('📊 New selected IDs:', newIds);
      return newIds;
    });
  }, []);

  const handleSubmit = async () => {
    if (selectedMetricIds.length === 0) {
      toast.error('Bitte wähle mindestens eine Leistung aus');
      return;
    }

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create performance entries for each selected metric
      const performanceEntries = selectedMetricIds.map(metricId => {
        const metric = metrics.find(m => m.id === metricId);
        return {
          metric_id: metricId,
          user_id: user.id,
          points: metric?.points_value || 1,
          notes: notes || null,
          unit_id: unitId || null,
          recorded_by: user.id,
          recorded_at: new Date().toISOString()
        };
      });

      const { error } = await supabase
        .from('team_member_performance')
        .insert(performanceEntries);

      if (error) throw error;

      const totalPoints = performanceEntries.reduce((sum, e) => sum + e.points, 0);
      toast.success(`${selectedMetricIds.length} Leistung(en) erfasst (+${totalPoints} Punkte)`);
      
      // Get selected metric names for callback
      const selectedMetricNames = selectedMetricIds
        .map(id => metrics.find(m => m.id === id)?.name)
        .filter(Boolean) as string[];
      
      // Reset form
      setSelectedMetricIds([]);
      setNotes('');
      
      onConfirm(selectedMetricNames, notes || undefined);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving performance:', error);
      toast.error('Fehler beim Speichern der Leistung');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPoints = selectedMetricIds.reduce((sum, id) => {
    const metric = metrics.find(m => m.id === id);
    return sum + (metric?.points_value ?? 1);
  }, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Leistung erfassen
          </DialogTitle>
          <DialogDescription>
            Wähle die erreichten Abschlüsse oder Leistungen für diese Einheit aus.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : metrics.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Keine Metriken definiert</p>
            <p className="text-xs mt-1">Dein Teamleiter muss zuerst Metriken erstellen.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Abschlüsse / Leistungen</Label>
              <div className="grid gap-2 max-h-[250px] overflow-y-auto pr-2">
                {metrics.map(metric => {
                  const MetricIcon = getIconByName(metric.icon || 'trophy');
                  const isSelected = selectedMetricIds.includes(metric.id);
                  
                    const pointsValue = metric.points_value ?? 1;
                    
                    return (
                      <div
                        key={metric.id}
                        role="button"
                        tabIndex={0}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all select-none ${
                          isSelected 
                            ? 'bg-primary/10 border-primary' 
                            : 'bg-card hover:bg-muted/50'
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleMetricToggle(metric.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleMetricToggle(metric.id);
                          }
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          className="pointer-events-none"
                        />
                        <div className="p-1.5 rounded bg-primary/10">
                          <MetricIcon className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{metric.name}</p>
                          {metric.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {metric.description}
                            </p>
                          )}
                        </div>
                        <Badge variant="secondary" className="shrink-0">
                          +{pointsValue} Pkt
                        </Badge>
                      </div>
                    );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notizen (optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Zusätzliche Informationen..."
                className="resize-none"
                rows={2}
              />
            </div>

            {selectedMetricIds.length > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10">
                <span className="text-sm font-medium">
                  {selectedMetricIds.length} Leistung(en) ausgewählt
                </span>
                <Badge className="bg-primary text-primary-foreground">
                  +{selectedPoints} Punkte
                </Badge>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Abbrechen
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || selectedMetricIds.length === 0}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Trophy className="mr-2 h-4 w-4" />
                Erfassen
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};